<script setup>
// Auto-extracted chrome for Solarheiztherm.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Solar-Heiztherm" },
    brandTagline: { type: String, default: "Sonne, die heizt." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Solar-Heiztherm");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Sonne, die heizt.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600;700&family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800;900&display=swap";
</script>

<template>
    <div class="sht" :style="`--sht-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="sht-nav">
                    <div class="sht-nav__inner">
                        <a class="sht-logo" href="/" aria-label="Startseite Solar-Heiztherm">
                            <span class="sht-logo__sun" aria-hidden="true"></span>
                            <span class="sht-logo__txt">
                                <span class="sht-logo__name">{{ brandName }}</span>
                                <span class="sht-logo__sub">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="sht-nav__links" aria-label="Sekundärnavigation">
                            <a href="#methodik">Methodik</a>
                            <a href="#faq">Fragen</a>
                            <a href="#sht-stepper" class="sht-nav__cta">Berechnen starten</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="sht-foot">
                    <div class="sht-foot__inner">
                        <div class="sht-foot__brand">
                            <span class="sht-logo__sun" aria-hidden="true"></span>
                            <span>{{ brandName }} &middot; {{ brandTagline }}</span>
                        </div>
                        <nav class="sht-foot__links" aria-label="Footer">
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                        <p class="sht-foot__copy">&copy; {{ new Date().getFullYear() }} solar-heiztherm.de &middot; alle Berechnungen ohne Gewähr, ersetzen keine Fachplanung.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* SHT v3.1 editorial - Linear/Stripe polish + Enpal/Thermondo conversion.
   sht-* namespace preserved per AGENT.md. Mobile-first 375 base.
   Space Grotesk display + Inter body. Orange brand = orange CTA.
   Hairline borders only, no heavy shadows, asymmetric editorial layouts. */

.sht {
    --sht-ink: #1f130a;
    --sht-ink-2: #4a3826;
    --sht-mute: #8a7359;
    --sht-cream: #fbf6ed;
    --sht-cream-2: #f4ead7;
    --sht-surface: #fff;
    --sht-line: rgba(31, 19, 10, 0.08);
    --sht-line-strong: rgba(31, 19, 10, 0.16);
    --sht-cta: #ea580c;
    --sht-cta-hover: #c2410c;
    --sht-cta-glow: 0 8px 24px -8px rgba(234,88,12,0.5), 0 0 0 1px rgba(234,88,12,0.2);
    --sht-cold: #92400e;
    --sht-ease: cubic-bezier(0.32, 0.72, 0, 1);

    background: var(--sht-cream);
    color: var(--sht-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 17px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100dvh;
    padding-bottom: 88px;
    text-rendering: optimizeLegibility;
}
.sht * { box-sizing: border-box; }
.sht a { color: inherit; }
.sht input, .sht button, .sht output { font-family: inherit; }
.sht-num-tab { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum'; }

/* ── Top nav ───────────────────────────────────────────────────── */
.sht-nav {
    background: rgba(251, 246, 237, 0.85);
    backdrop-filter: saturate(160%) blur(12px);
    -webkit-backdrop-filter: saturate(160%) blur(12px);
    border-bottom: 1px solid var(--sht-line);
    position: sticky;
    top: 0;
    z-index: 30;
}
.sht-nav__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.sht-logo { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; color: var(--sht-ink); }
.sht-logo__sun {
    width: 22px; height: 22px; border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #fed7aa, var(--sht-cta) 70%);
    display: inline-block; flex: none;
    box-shadow: 0 0 0 3px rgba(234,88,12,0.12);
}
.sht-logo__txt { display: flex; flex-direction: column; line-height: 1.15; }
.sht-logo__name { font-weight: 700; font-size: 15px; letter-spacing: -0.01em; }
.sht-logo__sub { font-size: 11px; color: var(--sht-mute); letter-spacing: 0.02em; }
.sht-nav__links { display: none; align-items: center; gap: 28px; font-size: 14px; }
.sht-nav__links a { text-decoration: none; color: var(--sht-ink-2); transition: color 180ms var(--sht-ease); }
.sht-nav__links a:hover { color: var(--sht-ink); }
.sht-nav__cta {
    padding: 9px 16px;
    background: var(--sht-ink);
    color: var(--sht-cream) !important;
    border-radius: 999px;
    font-weight: 600;
}
.sht-nav__cta:hover { background: var(--sht-cta); color: #fff !important; }
@media (min-width: 720px) { .sht-nav__links { display: inline-flex; } }

/* ── Hero ──────────────────────────────────────────────────────── */
.sht-hero { padding: 28px 20px 8px; position: relative; }
.sht-hero__inner {
    max-width: 1180px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 28px;
    align-items: stretch;
}
.sht-hero__kicker {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    color: var(--sht-ink-2);
    margin: 0 0 22px;
    padding: 8px 14px 8px 12px;
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 999px;
    letter-spacing: 0.01em;
    width: max-content;
}
.sht-hero__dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--sht-cta);
    box-shadow: 0 0 0 4px rgba(234,88,12,0.14);
}
.sht-hero__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(40px, 8.5vw, 80px);
    line-height: 1.0;
    letter-spacing: -0.035em;
    margin: 0 0 18px;
    color: var(--sht-ink);
    text-wrap: balance;
}
.sht-hero__em {
    font-style: normal;
    font-weight: 700;
    color: var(--sht-cta);
    letter-spacing: -0.035em;
}
.sht-hero__sub {
    font-size: clamp(16px, 2.2vw, 19px);
    line-height: 1.5;
    color: var(--sht-ink-2);
    margin: 0 0 28px;
    max-width: 56ch;
}
.sht-hero__ctaRow { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.sht-hero__meta, .sht-form__meta {
    font-size: 13px;
    font-weight: 500;
    color: var(--sht-mute);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-variant-numeric: tabular-nums;
    margin: 0;
}
.sht-form__meta { text-align: center; margin-top: 8px; }

/* Sample report card */
.sht-hero__sample { display: flex; align-items: flex-start; justify-content: center; }
.sht-sample {
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 20px;
    padding: 22px 22px 18px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 24px 60px -32px rgba(31,19,10,0.18);
    position: relative;
}
.sht-sample::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(234,88,12,0.18), rgba(234,88,12,0) 40%);
    pointer-events: none;
    mask: linear-gradient(#000, transparent 30%);
    -webkit-mask: linear-gradient(#000, transparent 30%);
}
.sht-sample__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.sht-sample__tag { font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--sht-mute); font-weight: 600; }
.sht-sample__dot { width: 9px; height: 9px; border-radius: 50%; background: var(--sht-cta); box-shadow: 0 0 0 4px rgba(234,88,12,0.14); }
.sht-sample__label { font-size: 12px; color: var(--sht-mute); margin: 0 0 4px; }
.sht-sample__big {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(40px, 8vw, 64px);
    letter-spacing: -0.045em;
    line-height: 1;
    margin: 0 0 18px;
    color: var(--sht-ink);
    font-variant-numeric: tabular-nums;
}
.sht-sample__bigPre { font-family: 'Inter', sans-serif; font-size: 14px; color: var(--sht-mute); font-weight: 400; vertical-align: 0.55em; margin-right: 6px; letter-spacing: 0; }
.sht-sample__bigCur { font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 600; color: var(--sht-cta); margin-left: 4px; vertical-align: 0.18em; }
.sht-sample__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
    color: var(--sht-ink-2);
    padding: 8px 0;
    border-top: 1px solid var(--sht-line);
    margin: 0;
}
.sht-sample__row span:last-child { font-weight: 700; color: var(--sht-ink); }
.sht-sample__row--strong span:last-child { color: var(--sht-cta); }
.sht-sample__foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--sht-line);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--sht-mute);
}
.sht-sample__foot svg { color: var(--sht-cta); flex: none; }

/* Trust hairline strip */
.sht-trust { margin-top: 36px; border-top: 1px solid var(--sht-line); border-bottom: 1px solid var(--sht-line); }
.sht-trust__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}
.sht-trust__item { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; font-size: 13px; }
.sht-trust__k { color: var(--sht-mute); letter-spacing: 0.12em; text-transform: uppercase; font-size: 11px; font-weight: 600; }
.sht-trust__v { color: var(--sht-ink); font-weight: 500; text-align: right; }

@media (min-width: 880px) {
    .sht-hero { padding: 56px 32px 16px; }
    .sht-hero__inner { grid-template-columns: 1.25fr 1fr; gap: 56px; }
    .sht-hero__sample { justify-content: flex-end; align-items: center; }
    .sht-sample { max-width: 440px; }
    .sht-trust__inner { grid-template-columns: repeat(3, 1fr); gap: 28px; padding: 22px 32px; }
    .sht-trust__item { flex-direction: column; align-items: flex-start; gap: 4px; }
    .sht-trust__v { text-align: left; }
}

/* ── Stepper ───────────────────────────────────────────────────── */
.sht-step { padding: 48px 20px 64px; }
.sht-step__inner { max-width: 820px; margin: 0 auto; }

.sht-step__head {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 28px;
}
.sht-step__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: var(--sht-cta);
    margin: 0;
    padding: 6px 12px;
    background: rgba(234,88,12,0.08);
    border: 1px solid rgba(234,88,12,0.18);
    border-radius: 999px;
    width: max-content;
    font-variant-numeric: tabular-nums;
}
.sht-step__titleWrap { min-width: 0; }
.sht-step__kicker { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--sht-mute); margin: 0 0 4px; font-weight: 600; }
.sht-step__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(24px, 4vw, 32px);
    letter-spacing: -0.025em;
    margin: 0;
    color: var(--sht-ink);
    line-height: 1.1;
}
.sht-step__prog {
    height: 3px;
    background: var(--sht-line);
    border-radius: 2px;
    overflow: hidden;
    margin-top: 8px;
}
.sht-step__progFill { display: block; height: 100%; background: var(--sht-cta); transition: width 320ms var(--sht-ease); }

.sht-chapter { display: flex; flex-direction: column; gap: 36px; }
.sht-field { display: flex; flex-direction: column; gap: 14px; }
.sht-field__lbl {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 16px;
    font-weight: 600;
    color: var(--sht-ink);
    letter-spacing: -0.005em;
}
.sht-field__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 13px;
    color: var(--sht-mute);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    min-width: 22px;
}

/* Radio cards */
.sht-radios { display: grid; gap: 10px; }
@media (min-width: 600px) {
    .sht-radios--2 { grid-template-columns: 1fr 1fr; }
    .sht-radios--4 { grid-template-columns: repeat(4, 1fr); }
}
.sht-radios--4 { grid-template-columns: repeat(2, 1fr); }
@media (min-width: 480px) { .sht-radios--4 { grid-template-columns: repeat(4, 1fr); } }

.sht-radio {
    appearance: none;
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 12px;
    padding: 14px 16px;
    text-align: left;
    cursor: pointer;
    color: var(--sht-ink);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 60px;
    transition: border-color 180ms var(--sht-ease), background 180ms var(--sht-ease), transform 100ms var(--sht-ease);
    font: inherit;
}
.sht-radio--compact { min-height: 48px; padding: 12px 12px; justify-content: center; align-items: center; text-align: center; }
.sht-radio:hover { border-color: var(--sht-line-strong); background: var(--sht-cream); }
.sht-radio:active { transform: translateY(1px); }
.sht-radio--on {
    border-color: var(--sht-cta);
    background: var(--sht-surface);
    box-shadow: 0 0 0 3px rgba(234,88,12,0.08);
    color: var(--sht-cta);
}
.sht-radio__bullet {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 1.5px solid var(--sht-line-strong);
    display: inline-flex; align-items: center; justify-content: center;
    margin-top: 1px; flex: none;
    background: var(--sht-surface);
    transition: border-color 180ms var(--sht-ease);
}
.sht-radio--on .sht-radio__bullet { border-color: var(--sht-cta); }
.sht-radio__bulletDot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--sht-cta);
    transform: scale(0);
    transition: transform 180ms var(--sht-ease);
}
.sht-radio--on .sht-radio__bulletDot { transform: scale(1); }
.sht-radio__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sht-radio__main { font-weight: 600; font-size: 15px; line-height: 1.3; }
.sht-radio__hint { font-size: 13px; color: var(--sht-mute); line-height: 1.4; font-weight: 400; }

/* Slider */
.sht-slider {
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 12px;
    padding: 18px 18px 14px;
}
.sht-slider__row { display: flex; align-items: center; gap: 16px; }
.sht-slider input[type=range] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    height: 28px;
    min-height: 44px;
}
.sht-slider input[type=range]::-webkit-slider-runnable-track {
    height: 4px; background: var(--sht-line-strong); border-radius: 2px;
}
.sht-slider input[type=range]::-moz-range-track {
    height: 4px; background: var(--sht-line-strong); border-radius: 2px;
}
.sht-slider input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--sht-cta);
    border: 3px solid var(--sht-surface);
    box-shadow: 0 1px 6px rgba(234,88,12,0.4);
    margin-top: -9px;
    cursor: grab;
}
.sht-slider input[type=range]::-moz-range-thumb {
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--sht-cta);
    border: 3px solid var(--sht-surface);
    box-shadow: 0 1px 6px rgba(234,88,12,0.4);
    cursor: grab;
}
.sht-slider input[type=range]:focus { outline: none; }
.sht-slider input[type=range]:focus::-webkit-slider-thumb { box-shadow: 0 0 0 4px rgba(234,88,12,0.18); }
.sht-slider output {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--sht-ink);
    min-width: 96px;
    text-align: right;
}
.sht-slider__ticks {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 11px;
    color: var(--sht-mute);
    letter-spacing: 0.04em;
}

/* Person counter */
.sht-stepper-input {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 999px;
    padding: 4px;
    width: max-content;
}
.sht-stepper-input button {
    background: transparent;
    border: 0;
    width: 44px;
    height: 44px;
    font-size: 22px;
    color: var(--sht-cta);
    font-weight: 700;
    border-radius: 50%;
    cursor: pointer;
    transition: background 180ms var(--sht-ease);
}
.sht-stepper-input button:hover { background: var(--sht-cream-2); }
.sht-stepper-input span {
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    padding: 0 20px;
    font-size: 24px;
    font-weight: 700;
    min-width: 60px;
    text-align: center;
    letter-spacing: -0.02em;
}

/* Text fields */
.sht-textfield {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 12px;
    padding: 4px 16px;
    transition: border-color 180ms var(--sht-ease), background 180ms var(--sht-ease);
}
.sht-textfield:focus-within { border-color: var(--sht-cta); box-shadow: 0 0 0 3px rgba(234,88,12,0.08); }
.sht-textfield input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 18px;
    font-weight: 600;
    padding: 14px 0;
    color: var(--sht-ink);
    min-height: 48px;
    font-variant-numeric: tabular-nums;
}
.sht-textfield__unit { font-size: 13px; color: var(--sht-mute); white-space: nowrap; }
.sht-textfield--plz input { letter-spacing: 0.08em; }

.sht-step__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-top: 24px;
    margin-top: 8px;
    border-top: 1px solid var(--sht-line);
}

/* ── Buttons ───────────────────────────────────────────────────── */
.sht-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: inherit;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: -0.005em;
    line-height: 1;
    padding: 14px 20px;
    min-height: 48px;
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    transition: background 180ms var(--sht-ease), color 180ms var(--sht-ease), border-color 180ms var(--sht-ease), transform 100ms var(--sht-ease);
    text-decoration: none;
    white-space: nowrap;
}
.sht-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.sht-btn:active:not(:disabled) { transform: translateY(1px); }
.sht-btn__arr { transition: transform 220ms var(--sht-ease); }
.sht-btn:not(:disabled):hover .sht-btn__arr { transform: translateX(3px); }

.sht-btn--primary {
    background: var(--sht-cta);
    color: #fff;
    box-shadow: var(--sht-cta-glow);
    font-weight: 700;
    letter-spacing: -0.012em;
    transition: background 180ms var(--sht-ease), box-shadow 200ms var(--sht-ease), transform 180ms var(--sht-ease);
}
.sht-btn--primary:hover:not(:disabled) {
    background: var(--sht-cta-hover);
    box-shadow: 0 12px 32px -8px rgba(234,88,12,0.6), 0 0 0 1px rgba(234,88,12,0.25);
    transform: translateY(-1px);
}
.sht-btn--ghost { background: transparent; color: var(--sht-ink); border-color: var(--sht-line-strong); }
.sht-btn--ghost:hover:not(:disabled) { background: var(--sht-surface); border-color: var(--sht-ink); }
.sht-btn--lg { padding: 22px 32px; font-size: 19px; min-height: 60px; font-weight: 800; letter-spacing: -0.01em; }
.sht-btn--full { width: 100%; }
.sht-btn--stick { padding: 12px 20px; min-height: 48px; font-size: 15px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
.sht-btn--shout {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 900;
    font-size: 19px;
}
@media (min-width: 720px) {
    .sht-btn--shout { font-size: 22px; letter-spacing: 0.09em; }
}

.sht-back-link {
    background: transparent;
    border: 0;
    color: var(--sht-mute);
    font-size: 14px;
    font-weight: 500;
    padding: 12px 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: color 180ms var(--sht-ease);
    min-height: 44px;
}
.sht-back-link:hover { color: var(--sht-ink); }

/* ── Result ────────────────────────────────────────────────────── */
.sht-result {
    background: var(--sht-surface);
    border: 1px solid var(--sht-line);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}
.sht-result::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: var(--sht-cta);
}
.sht-result__inner { padding: 36px 26px 28px; }

.sht-money { padding-bottom: 24px; border-bottom: 1px solid var(--sht-line); }
.sht-money__label { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--sht-mute); margin: 0 0 14px; font-weight: 600; }
.sht-money__value {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(58px, 12vw, 104px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    margin: 0 0 12px;
    color: var(--sht-ink);
    font-variant-numeric: tabular-nums;
    text-wrap: balance;
}
.sht-money__value--alt { font-size: clamp(34px, 6vw, 52px); letter-spacing: -0.03em; }
.sht-money__pre {
    font-family: 'Inter', sans-serif;
    font-size: clamp(16px, 2.4vw, 22px);
    color: var(--sht-mute);
    font-weight: 400;
    letter-spacing: 0;
    margin-right: 10px;
    vertical-align: 0.55em;
}
.sht-money__num { color: var(--sht-cta); }
.sht-money__cur {
    font-family: 'Inter', sans-serif;
    font-size: clamp(28px, 5vw, 44px);
    color: var(--sht-cta);
    font-weight: 600;
    margin-left: 10px;
    vertical-align: 0.16em;
    letter-spacing: 0;
}
.sht-money__hint { font-size: 14px; color: var(--sht-ink-2); margin: 0 0 16px; }

.sht-tier {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px 8px 12px;
    background: var(--sht-cream);
    border: 1px solid var(--sht-line);
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    color: var(--sht-ink);
}
.sht-tier__pip { width: 10px; height: 10px; border-radius: 50%; }
.sht-tier--t3 .sht-tier__pip { background: var(--sht-cta); box-shadow: 0 0 0 3px rgba(234,88,12,0.18); }
.sht-tier--t2 .sht-tier__pip { background: #d97706; box-shadow: 0 0 0 3px rgba(217,119,6,0.18); }
.sht-tier--t1 .sht-tier__pip { background: var(--sht-cold); box-shadow: 0 0 0 3px rgba(146,64,14,0.18); }

.sht-kpis {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    margin: 24px 0 24px;
    border-top: 1px solid var(--sht-line);
    border-bottom: 1px solid var(--sht-line);
}
.sht-kpis div {
    padding: 18px 18px;
    border-right: 1px solid var(--sht-line);
    border-bottom: 1px solid var(--sht-line);
}
.sht-kpis div:nth-child(2n) { border-right: 0; }
.sht-kpis div:nth-last-child(-n+2) { border-bottom: 0; }
.sht-kpis dt { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--sht-mute); margin-bottom: 8px; font-weight: 600; }
.sht-kpis dd {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.025em;
    color: var(--sht-ink);
    line-height: 1;
}
.sht-kpis dd span { font-family: 'Inter', sans-serif; font-size: 13px; color: var(--sht-mute); font-weight: 500; margin-left: 4px; letter-spacing: 0; }
.sht-kpis__win { color: var(--sht-cta) !important; }

@media (min-width: 640px) {
    .sht-kpis { grid-template-columns: repeat(4, 1fr); }
    .sht-kpis div:nth-child(2n) { border-right: 1px solid var(--sht-line); }
    .sht-kpis div:nth-child(4n) { border-right: 0; }
    .sht-kpis div { border-bottom: 0 !important; }
}

.sht-method {
    margin-top: 8px;
    font-size: 13px;
    color: var(--sht-ink-2);
}
.sht-method summary {
    cursor: pointer;
    color: var(--sht-ink);
    font-weight: 600;
    padding: 8px 0;
    list-style: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.sht-method summary::after { content: '+'; font-size: 18px; color: var(--sht-mute); transition: transform 180ms var(--sht-ease); display: inline-block; }
.sht-method[open] summary::after { transform: rotate(45deg); }
.sht-method ul { margin: 8px 0 0; padding-left: 20px; }
.sht-method li { margin: 6px 0; line-height: 1.55; }

/* Lead form */
.sht-lead {
    padding: 32px 26px 28px;
    background: var(--sht-cream);
    border-top: 1px solid var(--sht-line);
}
.sht-lead__head { margin-bottom: 24px; }
.sht-lead__eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--sht-cta); margin: 0 0 6px; font-weight: 700; }
.sht-lead__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(22px, 3.4vw, 28px);
    letter-spacing: -0.025em;
    margin: 0 0 8px;
    line-height: 1.15;
    color: var(--sht-ink);
    text-wrap: balance;
}
.sht-lead__sub { font-size: 15px; color: var(--sht-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.sht-form { display: flex; flex-direction: column; gap: 16px; }
.sht-form__grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 560px) { .sht-form__grid { grid-template-columns: 1fr 1fr; } }

.sht-input { display: flex; flex-direction: column; gap: 6px; }
.sht-input__lbl { font-size: 13px; font-weight: 600; color: var(--sht-ink); }
.sht-input__lbl em { font-weight: 400; font-style: italic; color: var(--sht-mute); }
.sht-input input {
    font-size: 16px;
    padding: 14px 16px;
    min-height: 50px;
    border: 1px solid var(--sht-line-strong);
    border-radius: 10px;
    background: var(--sht-surface);
    color: var(--sht-ink);
    transition: border-color 180ms var(--sht-ease), box-shadow 180ms var(--sht-ease);
}
.sht-input input:focus { outline: 0; border-color: var(--sht-cta); box-shadow: 0 0 0 3px rgba(234,88,12,0.12); }
.sht-input--err input { border-color: #b91c1c; }
.sht-input__err { font-size: 12px; color: #b91c1c; margin: 4px 0 0; font-weight: 500; }
.sht-input__err--center { text-align: center; margin-top: 8px; }

.sht-consent {
    display: grid;
    grid-template-columns: 22px 1fr;
    gap: 12px;
    align-items: flex-start;
    font-size: 12px;
    color: var(--sht-mute);
    line-height: 1.55;
    padding: 16px 0 4px;
    border-top: 1px solid var(--sht-line);
    margin-top: 4px;
}
.sht-consent input {
    width: 20px; height: 20px;
    margin-top: 2px;
    accent-color: var(--sht-cta);
    cursor: pointer;
}
.sht-consent__txt { display: block; }
.sht-consent--err { color: #b91c1c; }

.sht-done {
    padding: 48px 28px;
    background: var(--sht-cream);
    border-top: 1px solid var(--sht-line);
    text-align: center;
    color: var(--sht-ink);
}
.sht-done svg { color: var(--sht-cta); margin-bottom: 16px; }
.sht-done h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 26px;
    letter-spacing: -0.025em;
    margin: 0 0 10px;
}
.sht-done p { font-size: 15px; color: var(--sht-ink-2); margin: 0 auto; max-width: 46ch; }
.sht-done code { font-family: ui-monospace, monospace; background: var(--sht-cream-2); padding: 2px 6px; font-size: 13px; border-radius: 4px; }

.sht-altCold {
    padding: 28px 26px;
    background: var(--sht-cream);
    border-top: 1px solid var(--sht-line);
}
.sht-altCold h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 22px;
    letter-spacing: -0.025em;
    margin: 0 0 10px;
}
.sht-altCold p { font-size: 14px; color: var(--sht-ink-2); margin: 0 0 16px; }
.sht-altCold__links { list-style: none; padding: 0; margin: 0 0 18px; display: flex; flex-direction: column; gap: 0; }
.sht-altCold__links li { border-top: 1px solid var(--sht-line); }
.sht-altCold__links li:last-child { border-bottom: 1px solid var(--sht-line); }
.sht-altCold__links a {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
    color: var(--sht-ink);
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    transition: color 180ms var(--sht-ease);
}
.sht-altCold__links a:hover { color: var(--sht-cta); }
.sht-altCold__arr { color: var(--sht-mute); transition: transform 220ms var(--sht-ease); }
.sht-altCold__links a:hover .sht-altCold__arr { transform: translateX(3px); color: var(--sht-cta); }

.sht-result__back { padding: 0 26px 22px; }

/* ── Methodology editorial ─────────────────────────────────────── */
.sht-meth {
    padding: 72px 20px 56px;
    background: var(--sht-surface);
    border-top: 1px solid var(--sht-line);
    border-bottom: 1px solid var(--sht-line);
}
.sht-meth__inner { max-width: 1080px; margin: 0 auto; }
.sht-meth__head { margin-bottom: 36px; max-width: 780px; }
.sht-meth__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--sht-cta); margin: 0 0 12px; font-weight: 700; }
.sht-meth__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(28px, 5vw, 48px);
    letter-spacing: -0.03em;
    line-height: 1.05;
    margin: 0 0 14px;
    color: var(--sht-ink);
    text-wrap: balance;
}
.sht-meth__h em { font-style: normal; font-weight: 700; color: var(--sht-cta); }
.sht-meth__lead { font-size: 17px; color: var(--sht-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.sht-meth__list { list-style: none; padding: 0; margin: 0; }
.sht-meth__row {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 20px;
    padding: 28px 0;
    border-top: 1px solid var(--sht-line);
    align-items: start;
}
.sht-meth__row:last-child { border-bottom: 1px solid var(--sht-line); }
.sht-meth__rowNum {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: var(--sht-mute);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    margin-top: 4px;
}
.sht-meth__row h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(20px, 2.6vw, 24px);
    letter-spacing: -0.02em;
    margin: 0 0 8px;
    line-height: 1.25;
}
.sht-meth__row p { font-size: 15px; color: var(--sht-ink-2); margin: 0; line-height: 1.55; max-width: 60ch; }
.sht-meth__row p strong { color: var(--sht-ink); font-weight: 600; }
.sht-meth__rowTag {
    display: none;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--sht-mute);
    font-weight: 600;
    padding: 6px 12px;
    border: 1px solid var(--sht-line);
    border-radius: 999px;
    background: var(--sht-cream);
    align-self: start;
    margin-top: 6px;
    white-space: nowrap;
}
@media (min-width: 860px) {
    .sht-meth__row { grid-template-columns: 56px 1fr auto; gap: 32px; }
    .sht-meth__rowTag { display: inline-flex; align-items: center; }
}
.sht-meth__fineprint { font-size: 13px; color: var(--sht-mute); margin: 24px 0 0; line-height: 1.55; max-width: 64ch; }

/* ── FAQ ───────────────────────────────────────────────────────── */
.sht-faq { padding: 72px 20px 88px; }
.sht-faq__inner { max-width: 880px; margin: 0 auto; }
.sht-faq__head { margin-bottom: 32px; }
.sht-faq__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--sht-cta); margin: 0 0 12px; font-weight: 700; }
.sht-faq__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(28px, 5vw, 44px);
    letter-spacing: -0.03em;
    line-height: 1.1;
    margin: 0;
    text-wrap: balance;
}
.sht-faq__h em { font-style: normal; font-weight: 700; color: var(--sht-cta); }
.sht-faq__list { margin: 0; padding: 0; }
.sht-faq__item { padding: 22px 0; border-top: 1px solid var(--sht-line); }
.sht-faq__item:last-child { border-bottom: 1px solid var(--sht-line); }
.sht-faq__item dt {
    display: flex;
    gap: 14px;
    font-size: 17px;
    font-weight: 600;
    color: var(--sht-ink);
    margin-bottom: 8px;
    line-height: 1.35;
    letter-spacing: -0.01em;
}
.sht-faq__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: var(--sht-mute);
    min-width: 24px;
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
}
.sht-faq__item dd { margin: 0 0 0 38px; font-size: 15px; color: var(--sht-ink-2); line-height: 1.55; max-width: 60ch; }

/* ── Footer ────────────────────────────────────────────────────── */
.sht-foot { background: var(--sht-ink); color: rgba(251,246,237,0.7); padding: 36px 20px 40px; }
.sht-foot__inner { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 18px; }
.sht-foot__brand { display: inline-flex; align-items: center; gap: 12px; color: var(--sht-cream); font-size: 14px; font-weight: 600; }
.sht-foot__links { display: flex; gap: 24px; font-size: 14px; }
.sht-foot__links a { color: rgba(251,246,237,0.7); text-decoration: none; transition: color 180ms var(--sht-ease); }
.sht-foot__links a:hover { color: var(--sht-cream); }
.sht-foot__copy { font-size: 12px; color: rgba(251,246,237,0.45); margin: 0; line-height: 1.55; max-width: 64ch; }
@media (min-width: 720px) {
    .sht-foot__inner { grid-template-columns: auto 1fr auto; align-items: center; gap: 24px; }
    .sht-foot__copy { grid-column: 1 / -1; padding-top: 18px; border-top: 1px solid rgba(251,246,237,0.1); }
}

/* ── Sticky CTA ────────────────────────────────────────────────── */
.sht-stick {
    position: fixed;
    left: 12px; right: 12px;
    bottom: 12px;
    z-index: 40;
    background: var(--sht-ink);
    color: var(--sht-cream);
    border-radius: 14px;
    padding: 10px 12px 10px 18px;
    padding-bottom: max(10px, env(safe-area-inset-bottom, 10px));
    box-shadow: 0 18px 40px -16px rgba(31,19,10,0.5);
    border: 1px solid rgba(251,246,237,0.08);
}
.sht-stick__inner { display: flex; align-items: center; justify-content: space-between; gap: 14px; max-width: 720px; margin: 0 auto; }
.sht-stick__txt { display: flex; flex-direction: column; line-height: 1.2; font-size: 13px; min-width: 0; }
.sht-stick__txt strong { font-size: 14px; font-weight: 700; color: var(--sht-cream); }
.sht-stick__txt span { font-size: 12px; color: rgba(251,246,237,0.6); }
.sht-btn--stick { background: var(--sht-cta); color: #fff; border-color: var(--sht-cta); flex: none; }
.sht-btn--stick:hover:not(:disabled) { background: var(--sht-cta-hover); }
@media (min-width: 720px) { .sht-stick { display: none; } }

/* ── Real photographic hero (SHT: orange #EA580C accent frame, slim corners) ── */
.sht-hero-photo {
    margin: 0;
    width: 100%;
    max-width: 440px;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    border: 2px solid rgba(234, 88, 12, 0.55);
    box-shadow: 0 10px 30px -14px rgba(234, 88, 12, 0.28), 0 8px 32px -12px rgba(20, 17, 13, 0.18);
    background: #fff;
}
.sht-hero-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sht-hero-photo__caption {
    position: absolute;
    bottom: 12px;
    left: 12px;
    margin: 0;
    pointer-events: none;
}
.sht-hero-photo__chip {
    display: inline-block;
    background: #EA580C;
    color: #fff;
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 4px;
}

.sht-cite {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 18px;
    justify-content: center;
}
.sht-cite__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid rgba(234, 88, 12, 0.28);
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: #7c2d12;
    background: #fff;
}

.sht-byline {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px auto 0;
    max-width: 460px;
    justify-content: center;
}
.sht-byline__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #EA580C;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    font-weight: 600;
    font-size: 13px;
}
.sht-byline__txt { display: flex; flex-direction: column; line-height: 1.25; }
.sht-byline__name { font-size: 13px; font-weight: 600; color: var(--sht-ink, #1f1611); }
.sht-byline__role { font-size: 12px; color: var(--sht-mute, #75665a); }

.sht-install-card {
    margin: 28px 0;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid rgba(234, 88, 12, 0.22);
    box-shadow: 0 12px 36px -20px rgba(20, 17, 13, 0.22);
    background: #fff;
}
.sht-install-card img { width: 100%; height: auto; aspect-ratio: 2 / 1; object-fit: cover; display: block; }
.sht-install-card figcaption {
    font-size: 13px;
    color: var(--sht-mute, #75665a);
    padding: 12px 16px;
    border-top: 1px solid rgba(20, 17, 13, 0.06);
    background: #fff;
}

@media (prefers-reduced-motion: reduce) {
    .sht *, .sht *::before, .sht *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}

</style>
